import { useState, useEffect, useRef, useCallback } from 'react';
import { EventService } from 'app/services';

const useContentScroller = (scrollContainerRef) => {
  const [isBottom, setIsBottom] = useState(false);
  const [isTop, setIsTop] = useState(true);

  const isBottomRef = useRef(isBottom);
  const isTopRef = useRef(isTop);
  const frameRef = useRef(null);

  const updateState = useCallback((el) => {
    if (!el) return;

    const hasOverflow = el.scrollHeight > el.clientHeight + 1;

    const atBottom = hasOverflow && el.scrollTop + el.clientHeight >= el.scrollHeight - 5;

    const atTop = el.scrollTop <= 1;

    if (isBottomRef.current !== atBottom) {
      isBottomRef.current = atBottom;
      setIsBottom(atBottom);
    }

    if (isTopRef.current !== atTop) {
      isTopRef.current = atTop;
      setIsTop(atTop);
    }
  }, []);

  // Throttle bằng requestAnimationFrame
  const handleScroll = useCallback(
    (el) => {
      if (frameRef.current) return;

      frameRef.current = requestAnimationFrame(() => {
        updateState(el);
        frameRef.current = null;
      });
    },
    [updateState],
  );

  useEffect(() => {
    const el = scrollContainerRef?.current;

    // Case 1: có container thật
    if (el) {
      const onScroll = () => handleScroll(el);

      el.addEventListener('scroll', onScroll, { passive: true });

      // Re-check khi content resize (rất quan trọng cho DataTable)
      const resizeObserver = new ResizeObserver(() => {
        updateState(el);
      });

      resizeObserver.observe(el);

      // initial check
      updateState(el);

      return () => {
        el.removeEventListener('scroll', onScroll);
        resizeObserver.disconnect();
        if (frameRef.current) cancelAnimationFrame(frameRef.current);
      };
    }

    // Case 2: fallback qua EventService
    const onScroll = (e) => {
      const target = e.currentTarget;
      handleScroll(target);
    };

    EventService.on('contentScroll', onScroll);

    return () => {
      EventService.removeListener('contentScroll', onScroll);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [scrollContainerRef, handleScroll, updateState]);

  return {
    isBottom,
    isTop,
  };
};

export default useContentScroller;
